import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import {
	CreateDiagnosticQuestion,
	DiagnosticQuestion,
	UpdateDiagnosticQuestion,
} from '../../models/diagnosis.model';
import { DiagnosticQuestionsService } from '../diagnostic-questions.service';

@Injectable({
	providedIn: 'root'
})
export class DiagnosticQuestionsStore {

	private _questions = new BehaviorSubject<DiagnosticQuestion[] | null>(null);

	constructor(
		private diagnosticQuestionsService: DiagnosticQuestionsService,
		private toastrService: ToastService,
		private router: Router,
	) { }

	setQuestions(questions: DiagnosticQuestion[]) {
		this._questions.next(questions);
	}

	private reloadQuestions() {
		this.diagnosticQuestionsService.getAllQuestions()
			.pipe(
				catchError(err => {
					this.toastrService.error('Failed to load diagnostic questions');
					return of(false)
				})
			).subscribe(res => {
				if (res) {
					this.setQuestions(res as DiagnosticQuestion[]);
				}
			})
	}

	getQuestions(forceUpdate: boolean = false): Observable<DiagnosticQuestion[] | null> {
		if (this._questions.value !== null && !forceUpdate) {
			this._questions.next(this._questions.value);
		} else {
			this.reloadQuestions();
		}

		return this._questions.asObservable();
	}

	createQuestion(question: CreateDiagnosticQuestion) {
		this.diagnosticQuestionsService.createQuestion(question)
			.pipe(
				catchError(err => {
					console.log(err)
					this.toastrService.error('Failed to create diagnostic question');
					return of(null)
				})
			)
			.subscribe(res => {
				if (res !== null) {
					this.reloadQuestions();
					this.router.navigate(['/navbar/diagnostic-questions']);
					this.toastrService.success('Diagnostic question created');
				}
			})
	}

	updateQuestion(questionId: string, question: UpdateDiagnosticQuestion) {
		this.diagnosticQuestionsService.updateQuestion(questionId, question)
			.pipe(
				catchError(err => {
					console.log(err);
					this.toastrService.error('Failed to update diagnostic question');
					return of(false)
				})
			)
			.subscribe(res => {
				if (res !== false) {
					this.toastrService.success('Diagnostic question updated');
					this.reloadQuestions();
					this.router.navigate(['/navbar/diagnostic-questions']);
				}
			})
	}

	removeQuestion(questionId: string) {
		this.diagnosticQuestionsService.deleteQuestion(questionId)
			.pipe(
				catchError(err => {
					console.log(err);
					this.toastrService.error('Failed to delete diagnostic question');
					return of(false);
				})
			)
			.subscribe(res => {
				if (res !== false) {
					this.reloadQuestions();
					this.toastrService.success('Diagnostic question deleted');
				}
			})
	}

	publishQuestions(questionIds: string[], published: boolean) {
		this.diagnosticQuestionsService.publishQuestions({ questionIds, published })
			.pipe(
				catchError(err => {
					console.log(err);
					this.toastrService.error('Failed to publish diagnostic questions');
					return of(false);
				})
			)
			.subscribe(res => {
				if (res !== false) {
					this.toastrService.success(published ? 'Diagnostic questions published' : 'Diagnostic questions unpublished');
					this.reloadQuestions();
				}
			})
	}
}
